import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  InputRef,
  message,
  Modal,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import LicenseOverviewFunction from "../../Functions/LicenseOverview/LicenseOverview";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";
import { ColumnsType } from "antd/es/table";
import {
  SearchOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Loader from "../../Components/GlobalLoader/Loader";
import { format } from "date-fns";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { SearchFilter, WarningFilter } from "./TableFilters";

interface DataType {
  id: number;
  custId: number;
  licenseId: number;
  licenseDescription: string;
  warning: string;
  assignDate: string;
  lastOffDate: string;
}

interface FilterParams {
  id?: number;
  licenseId?: number;
  assignDate?: string;
  lastOffDate?: string;
  custId?: number;
  licenseDescription?: string;
  warning?: string;
  warningOrderDir?: string;
}

export type DataIndex = keyof DataType;

const LicenseOverviewTable: React.FC = () => {
  const [customerLicenses, setCustomerLicenses] = React.useState<DataType[]>(
    []
  );
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 20,
    pageSizeOptions: ["5", "20"],
    position: ["bottomCenter"],
  } as TablePaginationConfig);
  const [filterData, setFilterData] = useState<FilterParams>({});
  const [activeFilter, setActiveFilter] = useState<DataIndex | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const searchInput = useRef<InputRef>(null);
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const sessionToken = useSelector((state: RootState) => state.user.token);

  const handleForceUpdate = () => {
    setUpdateTrigger((prev) => !prev);
  };

  const fetchData = async () => {
    if (!accessToken || !sessionToken) {
      message.error("Authentication failed! Please login again.");
      return;
    }

    try {
      const response = await LicenseOverviewFunction.getAllLicenseExpirations(
        accessToken,
        sessionToken,
        {
          page: pagination.current?.toString(),
          pageSize: pagination.pageSize?.toString(),
          ...filterData,
        }
      );
      if (response.statusCode === 200) {
        setCustomerLicenses(response.data.customerLicenses);
        setPagination({ ...pagination, total: response.data.totalRows });
      } else {
        message.error("Failed to fetch licenses!");
      }
    } catch (error) {
      message.error("Failed to fetch licenses!");
    }
  };
  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [JSON.stringify(filterData), JSON.stringify(pagination), updateTrigger]);

  const handleChange = (
    pagination: TablePaginationConfig,
    filters: any,
    sorter: any
  ) => {
    setPagination(pagination);
    if (sorter.field === "warning") {
      if (sorter.order) {
        setFilterData({
          ...filterData,
          warningOrderDir: sorter.order === "ascend" ? "asc" : "desc",
        });
      } else {
        const newFilterData = { ...filterData };
        delete newFilterData.warningOrderDir;
        setFilterData(newFilterData);
      }
    }
  };

  const handleDeleteCustomerLicense = async (customerLicenseId: number) => {
    if (!accessToken || !sessionToken) {
      message.error("Authentication failed! Please login again.");
      return;
    }
    try {
      const response = await LicenseOverviewFunction.removeCustomerLicense(
        accessToken,
        sessionToken,
        customerLicenseId
      );
      if (response.status) {
        message.success("License deleted successfully", 5);
        handleForceUpdate();
      } else {
        message.error("Error deleting license");
      }
    } catch (error) {
      console.log("Error deleting license: ", error);
      message.error("Error deleting license");
    }
  };

  const popupConfirmDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this license?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      maskClosable: true,
      cancelText: "No",
      onOk() {
        return handleDeleteCustomerLicense(id);
      },
    });
  };

  const handleReset = (
    clearFilters: any,
    dataIndex: DataIndex,
    confirm: any
  ) => {
    delete filterData[dataIndex];
    clearFilters();
    confirm();
    setActiveFilter(null);
  };

  const handleSearch = (
    selectedKeys: any,
    confirm: any,
    dataIndex: DataIndex
  ) => {
    confirm();
    const searchValue = selectedKeys[0];
    setActiveFilter(null);

    if (searchValue) {
      setFilterData({ ...filterData, [dataIndex]: searchValue });
    } else {
      const newFilterData = { ...filterData };
      delete newFilterData[dataIndex];
      setFilterData(newFilterData);
    }
  };

  const getColumnSearchProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        {dataIndex === "warning" ? (
          <WarningFilter
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          handleSearch={handleSearch}
          handleReset={handleReset}
          dataIndex={dataIndex}
          />

        ) : (
          <SearchFilter 
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          handleSearch={handleSearch}
          handleReset={handleReset}
          dataIndex={dataIndex}
          />
        )}
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
        setActiveFilter(dataIndex);
      } else {
        setActiveFilter(null);
      }
    },
    filterDropdownVisible: activeFilter === dataIndex,
  });

  const columns: ColumnsType<DataType> = useMemo(
    () => [
      {
        title: "License ID",
        dataIndex: "id",
        key: "id",
        align: "center",
        responsive: ["lg"],
        ...getColumnSearchProps("id"),
        ellipsis: true,
      },
      {
        title: "Customer ID",
        dataIndex: "custId",
        key: "custId",
        align: "center",
        ...getColumnSearchProps("custId"),
        ellipsis: true,
      },
      {
        title: "License Description",
        dataIndex: "licenseDescription",
        key: "licenseDescription",
        align: "center",
        ...getColumnSearchProps("licenseDescription"),
        ellipsis: true,
        width: "20%",
      },
      {
        title: "Assign Date",
        dataIndex: "assignDate",
        key: "assignDate",
        align: "center",
        responsive: ["lg"],
        ellipsis: true,
        render: (text, record) => {
          if (!record.assignDate) return "";
          const expireDate = new Date(record.assignDate);
          return format(expireDate, "PPP");
        },
        width: "15%",
      },
      {
        title: "Expire Date",
        dataIndex: "lastOffDate",
        key: "lastOffDate",
        align: "center",
        ellipsis: true,
        responsive: ["md"],
        render: (text, record) => {
          if (!record.lastOffDate) return "";
          const expireDate = new Date(record.lastOffDate);
          return format(expireDate, "PPP");
        },
        width: "15%",
      },
      {
        title: "Status",
        dataIndex: "warning",
        key: "warning",
        align: "center",
        ellipsis: true,
        sorter: true,
        width: "10%",
        ...getColumnSearchProps("warning"),
        render: (text, record) => {
          switch (record.warning) {
            case "warning":
              return (
                <Tooltip title="License is expiring soon!">
                  <IoWarningOutline
                    style={{
                      color: "red",
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              );
            case "close":
              return (
                <Tooltip title="License is about to expire!">
                  <IoWarningOutline
                    style={{
                      color: "orange",
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              );
            default:
              return (
                <Tooltip title="License is active">
                  <IoCheckmarkCircleOutline
                    style={{
                      color: "#4BB543",
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              );
          }
        },
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        width: "5%",
        render: (text, record) => (
          <Tooltip title="Delete License">
            <Space size="middle">
              <DeleteOutlined
                key="delete"
                style={{ color: "#FF0000" }}
                onClick={(e) => {
                  e.stopPropagation();
                  popupConfirmDelete(record.id);
                }}
              />
            </Space>
          </Tooltip>
        ),
        align: "center",
        ellipsis: true,
      },
    ],
    [activeFilter]
  );

  return (
    <div className={"container-fluid"}>
      <div className={"row"}>
        <div className={"col-12 mt-3 mb-3"}>
          <Table
            dataSource={customerLicenses}
            columns={columns}
            pagination={pagination}
            onChange={handleChange}
            loading={{
              spinning: loading,
              indicator: (
                <div>
                  <Loader blue={true} size={"sm"} backgroundWeb={false} />
                </div>
              ),
            }}
            scroll={{ y: "758px", x: true }}
          />
        </div>
      </div>
    </div>
  );
};

export default LicenseOverviewTable;
